import React, { useCallback, useState, useEffect, useRef } from "react";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
  Input,
  Button,
} from "@coreai/component-library";
import { useTheme } from "@emotion/react";
import { useLanguage } from "LanguageContext";
import { patchAnalysis } from "services/analysisService";
import AutocompleteAnalysis from "../../../CommonUi/Autocomplete/AutocompleteAnalysis";
import { listProjects } from "services/projectsService";
import NotesField from "../FormComponents/NotesField";

interface EditAnalyseDialogProps {
  analyse: any;
  open: boolean;
  onClose: () => void;
  onEdit: (analysisId: string, updatedAnalysis: any) => void;
}

export default function EditAnalyseDialog(
  props: Readonly<EditAnalyseDialogProps>
) {
  const { t, setReRenderAnalysisList } = useLanguage();
  const theme = useTheme();
  const [campaignName, setCampaignName] = useState<string>(
    props.analyse.name || ""
  );
  const [notes, setNotes] = useState<string>(props.analyse.notes || "");
  const [projectId, setProjectId] = useState<string>(props.analyse.project?.id || "");
  const [advertisers, setAdvertisers] = useState<string[]>([]);
  const projectIdRef = useRef(props.analyse.project?.id);
  const [projectName, setProjectName] = useState<string>(props?.analyse?.project?.name+' - '+props?.analyse?.project?.advertiser || "");

  const alertDialogBodyStyles = {
    border: "none",
  } as React.CSSProperties;

  const bodyStyles = {
    fontFamily: theme.typography.bodyMedium.fontFamily,
    fontSize: theme.typography.bodyMedium.fontSize,
    fontWeight: theme.typography.bodyMedium.fontWeight,
    lineHeight: theme.typography.bodyMedium.lineHeight,
    color: theme.colors.textDark,
    marginBottom: theme.spacing.sm,
    paddingTop: 0,
    overflowY: "visible",
    gap: "1.5rem",
  } as React.CSSProperties;

  const onSave = useCallback(async () => {
    if (!props.analyse.id) return;
    try {
      const updatedAnalysis = {
        notes,
        project_id: projectIdRef.current,
        name:campaignName,
      };
      await patchAnalysis(props.analyse.id, updatedAnalysis);
      props.onEdit(props.analyse.id, updatedAnalysis);
      props.onClose();
      setReRenderAnalysisList(true);
    } catch (error) {
      console.error("Error updating analysis:", error);
    }
  }, [campaignName, notes, props]);

    function fetchAdvertisers() {
      listProjects({ sortingColumns: {} }).then((res: any) => {
        setAdvertisers(res.results)
      });
    }
  
    useEffect(() => {  
      fetchAdvertisers();
    }, []);

    useEffect(() => {  
      fetchAdvertisers();
    }, [projectId, projectName]);

    const updateProject = (data: any)=>{
      if(data?.id){
        projectIdRef.current = data?.id;
        setProjectId(data?.id);
      }else{
        setProjectName(data);
      }
    }
    const isSaveDisabled = () => {
      const initialProject = `${props?.analyse?.project?.name} - ${props?.analyse?.project?.advertiser}`;
      const isUnchanged = projectName === initialProject && campaignName === props?.analyse?.name && notes === props?.analyse?.notes;    
      const hasEmptyFields = projectName === "" || campaignName === "";
      return hasEmptyFields || isUnchanged;
    };
  return (
    <AlertDialog
      open={props.open}
      onOpenChange={(isOpen) => !isOpen && props.onClose()}
    >
      <AlertDialogContent size="sm">
        <AlertDialogHeader style={alertDialogBodyStyles}>
          <AlertDialogTitle>{t("core_editAnalysis")}</AlertDialogTitle>
        </AlertDialogHeader>

        <AlertDialogBody style={bodyStyles}>
          <div>
               <AutocompleteAnalysis
                  data={advertisers}
                  setAutocompleteValue={updateProject}
                  autocompleteLable={ t("alertDialog_add_project_label_advertiser")}
                  autocompletePlaceholder={""}
                  autocompleteValue={projectName}
                  addnewButtonPrefix={t("project_add_new")}
                  focuseNext={() => setAdvertisers}
                  props={{
                    name: "AdvertiserName",
                  }}
              />
          </div>

          <div>
            <Input
              name="Editcampaign"
              label={t("analyse_form_label_campaign")}
              value={campaignName}
              placeholder={t("analyse_form_placeholder_campaign")}
              style={{ width: "100%" }}
              onChange={(ev) => setCampaignName(ev.target.value)}
              autoComplete="off"
              // disabled
            />
          </div>
          <NotesField
            name="Editnote"
            value={notes}
            onChange={setNotes}
          />
        </AlertDialogBody>

        <AlertDialogFooter style={alertDialogBodyStyles}>
          <AlertDialogCancel asChild>
            <Button variant="tertiary">{t("core_cancel")}</Button>
          </AlertDialogCancel>
          <AlertDialogAction asChild>
            <Button
              name="editSave"
              variant={"brand"}
              style={{ backgroundColor: theme.colors.primary }}
              onClick={onSave}
              disabled={isSaveDisabled()}
     
            >
              {t("core_save")}
            </Button>
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
